import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight, Store, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Auth() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const { login, register, verifyEmail, resendCode } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // verification step
  const [step, setStep] = useState("form"); // form | verify
  const [verifyEmailAddr, setVerifyEmailAddr] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [emailSent, setEmailSent] = useState(false);

  const handleNeedsVerification = (data) => {
    setVerifyEmailAddr(data.email);
    setDevCode(data.dev_code || null);
    setEmailSent(!!data.email_sent);
    setCode("");
    setStep("verify");
    if (data.email_sent) toast.success("أرسلنا رمز تحقق إلى بريدك");
    else toast("استخدم الرمز الظاهر لتأكيد حسابك");
  };

  const submit = async () => {
    setBusy(true);
    try {
      let data;
      if (mode === "login") data = await login(form.email.trim(), form.password);
      else {
        if (!form.name.trim()) { toast.error("أدخل اسمك"); setBusy(false); return; }
        data = await register({ name: form.name.trim(), email: form.email.trim(), password: form.password, phone: form.phone.trim() });
      }
      if (data?.needsVerification) { handleNeedsVerification(data); setBusy(false); return; }
      toast.success("مرحبًا بك في كزيوني");
      nav(next);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async () => {
    if (code.trim().length !== 6) return toast.error("أدخل الرمز المكوّن من 6 أرقام");
    setBusy(true);
    try {
      await verifyEmail(verifyEmailAddr, code.trim());
      toast.success("تم تأكيد حسابك");
      nav(next);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      const data = await resendCode(verifyEmailAddr);
      setDevCode(data.dev_code || null);
      setEmailSent(!!data.email_sent);
      toast.success(data.email_sent ? "أعدنا إرسال الرمز إلى بريدك" : "تم توليد رمز جديد");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    }
  };

  return (
    <div dir="rtl">
      <div className="px-4 pt-4 pb-2">
        <button data-testid="back-btn" onClick={() => (step === "verify" ? setStep("form") : nav("/"))} className="k-tap w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}><ChevronRight size={20} /></button>
      </div>

      <div className="text-center px-8 pt-6 pb-4">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: "var(--k-ink)", color: "var(--k-gold)" }}>
          {step === "verify" ? <MailCheck size={26} /> : <Store size={26} />}
        </div>
        <h1 className="font-head text-[22px] font-black">كزيوني</h1>
        <p className="text-[12.5px] mt-1" style={{ color: "var(--k-ink-soft)" }}>سوق الجملة من تاجر لتاجر</p>
      </div>

      {step === "verify" ? (
        <div className="px-4" data-testid="verify-step">
          <p className="text-[13px] text-center mb-1" style={{ color: "var(--k-ink)" }}>أدخل رمز التحقق المرسل إلى</p>
          <p className="font-num text-[13px] text-center mb-4" style={{ color: "var(--k-ink-soft)", direction: "ltr" }}>{verifyEmailAddr}</p>

          {!emailSent && devCode && (
            <div data-testid="dev-code-notice" className="rounded-xl px-3 py-3 mb-4 text-center" style={{ background: "var(--k-gold-tint)", border: "1px dashed var(--k-gold)" }}>
              <p className="text-[11.5px] mb-1" style={{ color: "var(--k-gold-deep)" }}>وضع التجربة (لم يُربط مزوّد بريد بعد) — رمزك هو:</p>
              <p className="font-num text-[24px] font-bold tracking-widest" style={{ color: "var(--k-ink)" }}>{devCode}</p>
            </div>
          )}

          <input data-testid="verify-code-input" value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))} inputMode="numeric" placeholder="______" className="font-num w-full text-center text-[26px] font-bold tracking-[10px] rounded-xl py-3 mb-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }} onKeyDown={(e) => e.key === "Enter" && submitCode()} />

          <button data-testid="verify-submit" disabled={busy} onClick={submitCode} className="k-tap w-full py-3.5 rounded-2xl font-bold text-[14.5px] text-white mb-3" style={{ background: "var(--k-ink)", opacity: busy ? 0.7 : 1 }}>
            {busy ? "جارٍ التأكيد…" : "تأكيد الحساب"}
          </button>
          <button data-testid="resend-code" onClick={resend} className="k-tap w-full py-2.5 rounded-2xl font-bold text-[13px]" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)", color: "var(--k-ink-soft)" }}>
            إعادة إرسال الرمز
          </button>
        </div>
      ) : (
        <>
          <div className="flex gap-1 mx-4 p-1 rounded-2xl mb-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
            {[["login", "تسجيل الدخول"], ["register", "حساب جديد"]].map(([m, l]) => (
              <button key={m} data-testid={`tab-${m}`} onClick={() => setMode(m)} className="k-tap flex-1 py-2.5 rounded-xl text-[13px] font-bold" style={{ background: mode === m ? "var(--k-ink)" : "transparent", color: mode === m ? "#fff" : "var(--k-ink-soft)" }}>{l}</button>
            ))}
          </div>

          <div className="px-4">
            {mode === "register" && (
              <>
                <label className="text-[12.5px] font-semibold block mb-1.5">الاسم</label>
                <input data-testid="auth-name" value={form.name} onChange={set("name")} placeholder="اسمك الكامل" className="w-full rounded-lg px-3 py-2.5 text-[13.5px] mb-3" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }} />
              </>
            )}
            <label className="text-[12.5px] font-semibold block mb-1.5">البريد الإلكتروني</label>
            <input data-testid="auth-email" value={form.email} onChange={set("email")} type="email" placeholder="name@example.com" className="w-full rounded-lg px-3 py-2.5 text-[13.5px] mb-3" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }} />
            {mode === "register" && (
              <>
                <label className="text-[12.5px] font-semibold block mb-1.5">رقم الهاتف (اختياري)</label>
                <input data-testid="auth-phone" value={form.phone} onChange={set("phone")} type="tel" placeholder="+218 9x xxx xxxx" className="w-full rounded-lg px-3 py-2.5 text-[13.5px] mb-3" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }} />
              </>
            )}
            <label className="text-[12.5px] font-semibold block mb-1.5">كلمة المرور</label>
            <input data-testid="auth-password" value={form.password} onChange={set("password")} type="password" placeholder="••••••••" className="w-full rounded-lg px-3 py-2.5 text-[13.5px] mb-5" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }} onKeyDown={(e) => e.key === "Enter" && submit()} />

            <button data-testid="auth-submit" disabled={busy} onClick={submit} className="k-tap w-full py-3.5 rounded-2xl font-bold text-[14.5px] text-white" style={{ background: "var(--k-ink)", opacity: busy ? 0.7 : 1 }}>
              {busy ? "جارٍ…" : mode === "login" ? "دخول" : "إنشاء الحساب"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
